/**
 * useDailyStreak — gestion centralisée du streak d'activité quotidien.
 *
 * Responsabilités :
 *  - Lire / écrire le streak dans localStorage ('jogolinga-daily-streak')
 *  - Enregistrer la date du jour dans l'historique ('jogolinga-activity-history')
 *  - Gérer les boucliers de streak ('jogolinga-streak-shields', max 2)
 *  - Dispatcher l'event 'streakUpdated' pour que MainMenu et App.tsx réagissent
 *  - Exposer `recordActivity()` : à appeler une fois par session productive
 */

const DAILY_STREAK_KEY   = 'jogolinga-daily-streak';
const ACTIVITY_HISTORY_KEY = 'jogolinga-activity-history';
const STREAK_SHIELDS_KEY = 'jogolinga-streak-shields';

// Plafond dynamique : 2 shields jusqu'à 49 jours, 4 shields à partir de 50 jours
const getMaxShields = (streak: number): number => streak >= 50 ? 4 : 2;

interface DailyStreakData {
  date: string; // 'YYYY-MM-DD'
  streak: number;
}

const toDateString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Date figée au démarrage de l'app (chargement du module).
 * Toutes les opérations streak utilisent cette date pour éviter
 * qu'une session qui passe minuit soit attribuée au lendemain.
 */
export const SESSION_DATE = toDateString(new Date());

const sessionDateMinus1 = (): string => {
  const d = new Date(SESSION_DATE + 'T12:00:00');
  d.setDate(d.getDate() - 1);
  return toDateString(d);
};

/** Lit le nombre de boucliers disponibles (sans clamp — le plafond dépend du streak). */
export const readShields = (): number => {
  try {
    return Math.max(0, parseInt(localStorage.getItem(STREAK_SHIELDS_KEY) || '0', 10));
  } catch { return 0; }
};

const writeShields = (n: number, max: number): void => {
  try { localStorage.setItem(STREAK_SHIELDS_KEY, String(Math.max(0, Math.min(max, n)))); }
  catch { /* ignore */ }
};

/** Ajoute la date du jour dans l'historique d'activité (max 30 jours). */
export const recordActivityToday = (): void => {
  try {
    const today = SESSION_DATE;
    const raw = localStorage.getItem(ACTIVITY_HISTORY_KEY);
    const history: string[] = raw ? JSON.parse(raw) : [];
    if (!history.includes(today)) {
      const updated = [...history, today].slice(-30);
      localStorage.setItem(ACTIVITY_HISTORY_KEY, JSON.stringify(updated));
    }
  } catch { /* ignore */ }
};

/** Lit le streak actuel depuis localStorage (lecture pure, sans effets). */
export const readDailyStreak = (): number => {
  try {
    const raw = localStorage.getItem(DAILY_STREAK_KEY);
    if (!raw) return 0;
    return JSON.parse(raw).streak ?? 0;
  } catch {
    return 0;
  }
};

/** Vrai si le streak a déjà été compté pour la journée de session. */
export const isStreakCountedToday = (): boolean => {
  try {
    const raw = localStorage.getItem(DAILY_STREAK_KEY);
    if (!raw) return false;
    const data: DailyStreakData = JSON.parse(raw);
    return data.date === SESSION_DATE && data.streak > 0;
  } catch {
    return false;
  }
};

/**
 * Jours de grâce : nombre de jours manqués d'affilée qui ne cassent pas le
 * streak. Au-delà, un bouclier le protège s'il y en a un, sinon il repart à 1.
 * Le streak compte les jours actifs : un jour de grâce ne l'augmente pas.
 */
export const STREAK_GRACE_DAYS = 1;

const dayNumber = (date: string): number =>
  Math.round(new Date(date + 'T12:00:00').getTime() / 86400000);

/** Nombre de jours calendaires de `from` à `to` (1 = le lendemain). */
export const daysBetween = (from: string, to: string): number => dayNumber(to) - dayNumber(from);

/** Vrai si une dernière activité à `lastDate` laisse le streak en vie aujourd'hui. */
export const isStreakAlive = (lastDate: string, today: string = SESSION_DATE): boolean => {
  if (!lastDate) return false;
  const gap = daysBetween(lastDate, today);
  return gap >= 0 && gap <= 1 + STREAK_GRACE_DAYS;
};

/**
 * Jours actifs de la chaîne de streak, du plus récent au plus ancien, en
 * remontant depuis la dernière date ≤ `endDate` et en tolérant des trous
 * d'au plus STREAK_GRACE_DAYS jours. Ne dit pas si la chaîne est encore en
 * vie aujourd'hui : voir isStreakAlive(chain[0]).
 */
export const getStreakChain = (history: string[], endDate: string = SESSION_DATE): string[] => {
  const dates = Array.from(new Set(history)).filter(d => d <= endDate).sort();
  if (dates.length === 0) return [];
  const chain = [dates[dates.length - 1]];
  for (let i = dates.length - 2; i >= 0; i--) {
    if (daysBetween(dates[i], chain[chain.length - 1]) > 1 + STREAK_GRACE_DAYS) break;
    chain.push(dates[i]);
  }
  return chain;
};

/**
 * Boucliers nécessaires pour sauver un streak dont la dernière activité date de
 * `lastDate` : un par jour manqué au-delà des jours de grâce (0 s'il est en vie).
 */
export const shieldsNeeded = (lastDate: string, today: string = SESSION_DATE): number => {
  if (!lastDate) return Infinity;
  const missed = daysBetween(lastDate, today) - 1;
  return Math.max(0, missed - STREAK_GRACE_DAYS);
};

/** Vrai si le streak est en vie, ou peut encore être sauvé par les boucliers disponibles. */
export const isStreakSavable = (lastDate: string, today: string = SESSION_DATE): boolean =>
  isStreakAlive(lastDate, today) || shieldsNeeded(lastDate, today) <= readShields();

/**
 * Streak à afficher, sans effet de bord : le streak stocké s'il est encore en
 * vie (jour de grâce compris) ou si les boucliers peuvent le sauver, sinon 0.
 */
export const getDisplayedStreak = (): number => {
  try {
    const raw = localStorage.getItem(DAILY_STREAK_KEY);
    if (!raw) return 0;
    const data: DailyStreakData = JSON.parse(raw);
    return isStreakSavable(data.date) ? (data.streak ?? 0) : 0;
  } catch {
    return 0;
  }
};

/**
 * Streak à restaurer à la connexion, à partir de l'historique fusionné
 * (local + Supabase) et du compteur stocké (déjà fusionné par loadDailyData).
 * `broken` : la dernière activité est trop ancienne et les boucliers ne suffisent pas.
 */
export const computeRestoredStreak = (
  history: string[],
  profileStreak: number,
): { streak: number; lastActivityDate: string; broken: boolean } => {
  const chain = getStreakChain(history);
  const lastActivityDate = chain[0] ?? '';
  if (!isStreakSavable(lastActivityDate)) return { streak: 0, lastActivityDate, broken: true };

  let streak = chain.length;
  // L'historique ne garde que 30 dates : si la chaîne remonte jusqu'à la
  // plus ancienne, le vrai streak peut être plus long que ce qu'on compte.
  const oldest = [...history].sort()[0];
  if (history.length >= 30 && chain[chain.length - 1] === oldest) {
    streak = Math.max(streak, profileStreak || 0);
  }
  // Un bouclier comble un trou que getStreakChain ne franchit pas : la chaîne
  // s'arrête au trou et ne compte que les jours suivants. Si le compteur stocké
  // porte sur la même dernière activité, il fait foi — sinon chaque lancement de
  // l'app ramenait un streak sauvé par bouclier à quelques jours.
  try {
    const raw = localStorage.getItem(DAILY_STREAK_KEY);
    const stored: Partial<DailyStreakData> = raw ? JSON.parse(raw) : {};
    if (stored.date === lastActivityDate && (stored.streak ?? 0) > streak) {
      streak = stored.streak as number;
    }
  } catch { /* ignore */ }
  return { streak, lastActivityDate, broken: false };
};

/**
 * Tente de consommer les boucliers nécessaires (un par jour manqué au-delà de
 * la grâce) pour maintenir le streak ; sinon repart à 1.
 */
function applyShieldOrReset(
  prevStreak: number,
  lastActiveDate: string,
  today: string,
): { streak: number; isNewDay: boolean; shieldUsed: boolean } {
  const shields = readShields();
  const needed = shieldsNeeded(lastActiveDate, today);
  // Un streak déjà cassé (0) n'a rien à protéger : sans ce garde, le bouclier
  // était consommé pour « maintenir » 0, et le compteur restait bloqué à 0.
  if (prevStreak > 0 && needed > 0 && needed <= shields) {
    writeShields(shields - needed, getMaxShields(prevStreak));
    localStorage.setItem(DAILY_STREAK_KEY, JSON.stringify({ date: today, streak: prevStreak }));
    return { streak: prevStreak, isNewDay: true, shieldUsed: true };
  }
  localStorage.setItem(DAILY_STREAK_KEY, JSON.stringify({ date: today, streak: 1 }));
  return { streak: 1, isNewDay: true, shieldUsed: false };
}

/**
 * Calcule et persiste le nouveau streak après une session productive.
 * - Déjà compté aujourd'hui → retourne la valeur existante (idempotent)
 * - Dernière activité hier, ou avant-hier (jour de grâce) → streak + 1
 * - Plus de jours manqués + bouclier disponible → streak maintenu, bouclier consommé
 * - Sinon → repart à 1
 */
export const computeDailyStreak = (): { streak: number; isNewDay: boolean; shieldUsed: boolean } => {
  try {
    const today     = SESSION_DATE;
    const yesterday = sessionDateMinus1();
    const raw       = localStorage.getItem(DAILY_STREAK_KEY);

    if (!raw) {
      localStorage.setItem(DAILY_STREAK_KEY, JSON.stringify({ date: today, streak: 1 }));
      return { streak: 1, isNewDay: true, shieldUsed: false };
    }

    const data: DailyStreakData = JSON.parse(raw);

    // Déjà compté aujourd'hui — idempotent
    if (data.date === today && data.streak > 0) {
      return { streak: data.streak, isNewDay: false, shieldUsed: false };
    }

    const histRaw = localStorage.getItem(ACTIVITY_HISTORY_KEY);
    const history: string[] = histRaw ? JSON.parse(histRaw) : [];

    // Dernière activité dans la fenêtre de grâce (hier, ou avant-hier avec un
    // jour manqué) et historique cohérent → continuation normale
    if (data.streak > 0 && data.date < today && isStreakAlive(data.date) && history.includes(data.date)) {
      const next = { date: today, streak: data.streak + 1 };
      localStorage.setItem(DAILY_STREAK_KEY, JSON.stringify(next));
      return { streak: next.streak, isNewDay: true, shieldUsed: false };
    }

    // Compteur stocké désynchronisé (streak à 0, date obsolète, activité venue
    // d'un autre appareil…) : on reconstruit la chaîne depuis l'historique,
    // jours précédant aujourd'hui (recordActivity y a déjà ajouté aujourd'hui).
    const chain = getStreakChain(history, yesterday);
    if (chain.length > 0 && isStreakAlive(chain[0])) {
      const next = { date: today, streak: chain.length + 1 };
      localStorage.setItem(DAILY_STREAK_KEY, JSON.stringify(next));
      return { streak: next.streak, isNewDay: true, shieldUsed: false };
    }

    // Plus de jours manqués que la grâce → boucliers ou remise à zéro. Le streak
    // à protéger est repris de l'historique si le compteur stocké a déjà été
    // remis à 0 (sinon le bouclier n'était jamais utilisé).
    const lastActive = [chain[0] ?? '', data.date ?? ''].sort().pop() as string;
    return applyShieldOrReset(Math.max(data.streak, chain.length), lastActive, today);
  } catch {
    return { streak: 1, isNewDay: true, shieldUsed: false };
  }
};

/**
 * À appeler une fois par session productive (mots appris, règle maîtrisée…).
 * Enregistre la date du jour, calcule le streak, accorde un bouclier tous
 * les 7 jours de streak, dispatche 'streakUpdated', et annule le rappel du jour.
 */
export const recordActivity = (options: { silent?: boolean } = {}): number => {
  recordActivityToday();

  // Annuler la notification de rappel streak du jour (import dynamique pour éviter
  // la circularité et garantir que le module ne plante pas si Capacitor est absent)
  import('../services/notificationService').then(({ cancelStreakReminder }) => {
    cancelStreakReminder();
  }).catch(() => { /* silencieux */ });

  const { streak: newStreak, isNewDay, shieldUsed } = computeDailyStreak();

  // Bouclier gagné à chaque palier de 7 jours (7, 14, 21…)
  let shieldAwarded = false;
  if (isNewDay && newStreak > 0 && newStreak % 7 === 0 && !shieldUsed) {
    const maxShields = getMaxShields(newStreak);
    const current = readShields();
    if (current < maxShields) {
      writeShields(current + 1, maxShields);
      shieldAwarded = true;
    }
  }

  try {
    const rawHistory = localStorage.getItem(ACTIVITY_HISTORY_KEY);
    const activityHistory: string[] = rawHistory ? JSON.parse(rawHistory) : [];
    window.dispatchEvent(new CustomEvent('streakUpdated', {
      detail: {
        streak: newStreak,
        activityHistory,
        isNewDay,
        shieldUsed,
        shieldAwarded,
        shields: readShields(),
        // silent : rattrapage au chargement — pas de modal de célébration
        silent: options.silent === true,
      },
    }));
  } catch { /* ignore */ }
  return newStreak;
};
